import { useCallback, useState } from 'react';
import { BlockBlobClient } from '@azure/storage-blob';
import {
  CreateDocumentDto,
  DocumentUploadStage,
  extractErrorMessage,
  sleep,
  useCreateDocumentUploadUrl,
  useGetDocumentUploadStatus,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { FileItem } from '@sib/types';
import { v4 as uuidv4 } from 'uuid';
import { FileUploadWithCallbacksProps } from './types';
import {
  CHUNK_SIZE,
  STATUS_CHECK_INTERVAL,
  STATUS_RETRY_INTERVAL,
  MAX_RETRIES,
  isAbortError,
} from './utils';

interface UploadToBlobStorageProps extends FileUploadWithCallbacksProps {}

type PostprocessFileOptions = Pick<
  CreateDocumentDto,
  'batchId' | 'totalFiles'
> & {
  abortSignal?: AbortSignal;
};

export const useUploadToBlobStorage = ({
  onProgress,
  onError,
}: UploadToBlobStorageProps) => {
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();
  const { mutateAsync: createStorageUploadUrl } = useCreateDocumentUploadUrl({
    organizationId,
  });
  const { t } = useTranslation();

  const uploadFileToBlobStorage = useCallback(
    async (fileItem: FileItem, options: PostprocessFileOptions) => {
      const { file, isSensitive } = fileItem;

      let uploadUrl: string;
      const { abortSignal, ...uploadOptions } = options;

      try {
        onProgress?.(fileItem, 5);
        const { uploadUrl: url } = await createStorageUploadUrl({
          filename: file.name,
          filesize: file.size,
          mimetype: file.type,
          ...uploadOptions,
          sensitive: isSensitive ?? false,
        });
        uploadUrl = url;
      } catch (error) {
        return onError?.(
          fileItem,
          extractErrorMessage(error, t('fileManager.uploadFailedMessage'), {
            defaultMessageStatusCodes: [500, 404],
          }),
          error
        );
      }

      onProgress?.(fileItem, 10);

      try {
        const blobClient = new BlockBlobClient(uploadUrl);

        await blobClient.uploadData(file, {
          blockSize: CHUNK_SIZE,
          concurrency: 4,
          abortSignal,

          onProgress: ({ loadedBytes }) => {
            const uploadProgress = Math.round((loadedBytes / file.size) * 100);
            const totalProgress = 10 + Math.round((uploadProgress * 80) / 100);
            onProgress?.(fileItem, totalProgress);
          },
        });

        onProgress?.(fileItem, 95);
      } catch (error) {
        if (isAbortError(error)) return;

        onError?.(
          fileItem,
          extractErrorMessage(error, t('fileManager.uploadFailedMessage')),
          error
        );
      }
    },
    [onError, onProgress, createStorageUploadUrl, t]
  );

  return {
    uploadFileToBlobStorage,
  };
};

interface UseBatchUploadProps extends FileUploadWithCallbacksProps {
  getFiles: () => FileItem[];
}

export const useBatchUpload = ({
  getFiles,
  onSuccess,
  onError,
}: UseBatchUploadProps) => {
  const {
    organization: { id: organizationId },
  } = useUserSetupStore();
  const { open } = useNotification();
  const { mutateAsync: getDocumentUploadStatus } = useGetDocumentUploadStatus();
  const [retriesCount, setRetriesCount] = useState(0);
  const { t } = useTranslation();

  const generateBatchId = () => {
    const batchUploadId = uuidv4();

    return batchUploadId;
  };

  const checkForUploadStatus = useCallback(
    async (batchId: string, interval: number = STATUS_CHECK_INTERVAL) => {
      try {
        const { items } = await getDocumentUploadStatus({
          organizationId: organizationId || '',
          batchId,
        });
        const files = getFiles();

        files.forEach((fileItem: FileItem) => {
          const found = items.find(item => item.name === fileItem.file.name);
          if (!found || found.status === DocumentUploadStage.Pending) return;

          const isUploaded = found.status === DocumentUploadStage.Uploaded;

          if (isUploaded) return onSuccess?.({ ...fileItem, progress: 100 });

          onError?.(
            { ...fileItem, progress: 100 },
            t('fileManager.uploadFailedMessage')
          );
        });

        const areAllFilesProcessed = items.every(item =>
          [DocumentUploadStage.Failed, DocumentUploadStage.Uploaded].includes(
            item.status
          )
        );

        setRetriesCount(0);

        if (!areAllFilesProcessed) {
          await sleep(interval);

          return checkForUploadStatus(batchId, interval);
        }
      } catch (error) {
        if (retriesCount <= MAX_RETRIES) {
          setRetriesCount(prev => prev + 1);

          return checkForUploadStatus(batchId, STATUS_RETRY_INTERVAL);
        }
        open?.({
          type: 'error',
          message: t('fileManager.actions.checkUploadStatus.error.message'),
          description: t(
            'fileManager.actions.checkUploadStatus.error.description'
          ),
        });

        return error;
      }
    },
    [
      getDocumentUploadStatus,
      getFiles,
      onError,
      onSuccess,
      t,
      organizationId,
      retriesCount,
      open,
    ]
  );

  return {
    generateBatchId,
    checkForUploadStatus,
  };
};
